import React, { useState, useEffect } from 'react';
import { AppContext } from '../../App';
import { motion, AnimatePresence } from 'framer-motion';
import { CountryFlag } from '../../Utils/flags';
import { Cols } from '../Cols';
import { ModalSelect } from '../Modal/select';
import { ResultChart } from '../ResultChart';
import { refineChartData } from './refineChartData';
import { refineRadarChartData } from './refineRadarChartData';
import { inDelayTime } from '../../Utils/time';
import { DiveTeaserCard } from '../DiveTeaserCard';
import GuideAnimation from '../GuideAnimation';

import { dataFetcher } from '../../Utils/dataFetcher';
import { RadarChart } from '../RadarChart';

import athlete_skeleton from './athlete-no-image.png';
import style from './athlete-profile.module.scss';
import colStyle from '../Cols/cols.module.scss';
import { OnOffSwitch } from '../OnOffSwitch';
import TagManager from 'react-gtm-module';
import { getTrans } from '../../Utils/translate';
import { getAthleteIdById } from '../../Utils/crosslink';
import { CosmosImage, CosmosText } from '../../cosmos/web-scoped/react';
import { capitalize } from '../../Utils/string';
import { ModalComparison } from '../Modal/comparison';

const AthleteProfile = ({ config }) => {
  const context = React.useContext(AppContext);
  const animationConfig = context.config.animation;
  const uriConfig = context.config.uriConfig;
  const mode = context.data.mode;
  const lang = context.lang;
  const athleteAnimation = {
    initial: {
      opacity: 0,
      x: '80%',
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    },
    enter: {
      opacity: 1,
      x: '0%',
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    }
  };

  const [athletes, setAthletes] = useState([]);
  const [entrants, setEntrants] = useState(athletes.entrants);
  const [athleteId, setAthleteId] = useState(undefined);
  const [athlete, setAthlete] = useState(undefined);
  const [autoChange, setAutoChange] = useState(true);
  const [showDive, setShowDive] = useState(false);
  const [options, setOptions] = useState();

  useEffect(() => {
    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `loaded`,
        widgetType: `Athlete Profile - ${config.trackingTitle || config.navigationTitle}`
      }
    };

    TagManager.dataLayer(dataLayerArgs);

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dataFetcher(config.uriList, uriConfig)
      .then((data) => {
        data.entrants = data.entrants.sort((a, b) => {
          return `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` <
            `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
            ? -1
            : `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` >
              `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
            ? 1
            : 0;
        });
        setAthletes(data);
      })
      .catch(console.error);
  }, [config, uriConfig]);

  useEffect(() => {
    clearTimeout(window.timeoutActive);

    const fetchActive = async () => {
      const data = await dataFetcher(config.uriActive, uriConfig);

      if (autoChange) {
        if (
          (data.recent_finishers && data.recent_finishers.length >= 1) ||
          (data.active && data.active.length >= 1)
        ) {
          if (data.recent_finishers && data.recent_finishers.length >= 1) {
            const recentInDelayTime = data.recent_finishers
              .reverse()
              .filter((finisher) =>
                inDelayTime(
                  new Date(finisher.result_time),
                  config.delayDataSeconds || (60 && finisher)
                )
              );

            if (recentInDelayTime && recentInDelayTime.length >= 1) {
              // are there any recent finisher in spoiler time: yes
              const sorted = recentInDelayTime.sort((a, b) =>
                !a.start
                  ? 1
                  : !b.start || a.start < b.start
                  ? -1
                  : a.start > b.start
                  ? 1
                  : 0
              );
              // loaded
              setAthleteId(sorted[0].athlete_id);
            } else {
              // I added this case because of the data of a event. i think this case should never happen with normal data, but it did. so FIXME
              const sortedList = data.recent_finishers.sort((a, b) => b.start - a.start);
              setAthleteId(sortedList[0].athlete_id);
            }
          } else {
            // are there any recent finisher in spoiler time: no
            if (data && data.active && data.active.length >= 1) {
              // are there any active riders: yes
              const sorted = data.active.sort((a, b) =>
                !a.start
                  ? 1
                  : !b.start || a.start < b.start
                  ? -1
                  : a.start > b.start
                  ? 1
                  : 0
              );
              // loaded
              setAthleteId(sorted[0].athlete_id);
            } else {
              // are there any active riders: no
              if (data.recent_finishers && data.recent_finishers.length >= 1) {
                // are they any finished riders: yes
                const sorted = data.recent_finishers
                  .sort((a, b) =>
                    !a.start
                      ? 1
                      : !b.start || a.start < b.start
                      ? -1
                      : a.start > b.start
                      ? 1
                      : 0
                  )
                  .reverse();
                // loaded
                setAthleteId(sorted[0].athlete_id);
              } else {
                // are they any finished riders: no
                // no finished rider, get the first alphabetic one
                if (athletes.entrants) {
                  // loaded
                  setAthleteId(athletes.entrants[0].athlete.id);
                }
              }
            }
          }
        } else {
          // no finished rider, get the first alphabetic one
          if (athletes.entrants) {
            setAthleteId(athletes.entrants[0].athlete.id);
          }
        }
      }
      if (data && data.status && data.status === 'finished') {
        setAutoChange(false);
        return;
      }
    };

    let autoUpdate = undefined;
    if (!config.hasOwnProperty('initiateWithAthleteId')) {
      autoUpdate = setInterval(
          () => {
            fetchActive();
          },
          config.pollRateSeconds ? config.pollRateSeconds * 1000 : 15000
      );
      fetchActive();
    }

    return () => {
      if (autoUpdate) {
        clearInterval(autoUpdate);
      }
    };
  }, [config, autoChange, athletes, uriConfig]);

  const getStanding = ({ rank }) => {
    if (!rank) return '';
    switch (parseInt(rank)) {
      case 1:
        return '1st';
      case 2:
        return '2nd';
      case 3:
        return '3rd';
      default:
        return `${rank}th`;
    }
  };
  const getDivesSince = (facts) => {
    const dives = facts.filter((obj) => {
      return obj.stat_type === 'DivesSince';
    });
    return dives.length !== 0 ? dives[0].fact : '';
  };

  const getStartsWins = (stats, type) => {
    if (stats.length > 0) {
      if (stats.find((e) => e.stat_type === 'Red Bull Rampage')) {
        const temp = stats.find((e) => e.stat_type === 'Red Bull Rampage');
        if (type === 'Start') {
          return temp.starts;
        }
        if (type === 'Win') {
          return temp.wins;
        }
        if (type === 'Top5') {
          return temp.top5;
        }
      }
      if (stats.find((e) => e.stat_type === type)) {
        const temp1 = stats.find((e) => e.stat_type === type);
        return temp1.fact;
      }

      if (stats.find((e) => e.stat_type === type)) {
        const temp2 = stats.find((e) => e.stat_type === type);
        return temp2.fact;
      }
    }

    return '';
  };

  let wcStats = undefined;
  let facts = undefined;
  let chartData = undefined;
  let radarData = undefined;

  if (athlete) {
    wcStats = Array.isArray(athlete.career_stats)
      ? athlete.career_stats.find(
          ({ stat_type }) =>
            stat_type === 'World Cup' ||
            stat_type === 'Cliff Diving World Series' ||
            stat_type === 'Crankworx' ||
            stat_type === 'Overall'
        )
      : '';

    facts = Array.isArray(athlete.career_stats)
      ? athlete.career_stats.filter(
          ({ stat_type, fact }) => stat_type === 'Further Facts' && fact !== ''
        )
      : '';

    chartData = refineChartData({
      data: athlete.recent_events || [],
      config
    });
    radarData = refineRadarChartData({
      data: athlete.athlete_kpis || []
    });
  }

  useEffect(() => {
    if (athleteId) {
      dataFetcher(config.uriDetail.replace('{id}', athleteId), uriConfig)
        .then((data) => {
          if (!data.hasOwnProperty('career_stats')) {
            setAthlete({ ...data, career_stats: [] });
          } else setAthlete(data);
        })
        .catch(console.error);
    }
  }, [athleteId, config, uriConfig]);

  useEffect(() => {
    setEntrants(athletes.entrants);
  }, [athletes]);

  useEffect(() => {
    const options = entrants && entrants.map(({ athlete }) => {
      const athleteData = athlete.hasOwnProperty('athlete') ? athlete.athlete : athlete;
      return {
        label: (
          <>
            {config.showSelectAthleteImage && (
              <CosmosImage
                class="option-image"
                image={athleteData.image ? (athleteData.image.profile || athlete_skeleton) : athlete_skeleton}
                image-alt={`image-${athleteData.first_name}-${athleteData.last_name}`}/>
            )}
            <CosmosText
              class="option-text"
              kind="subtle">
              <img
                className="flag"
                src={CountryFlag(athleteData.nationality.iso3)}
                alt={athleteData.nationality.iso3}
              />
              {`${capitalize(athleteData.first_name)} ${capitalize(athleteData.last_name)}`}
            </CosmosText>
          </>
        ),
        athlete_id: athleteData.id,
        value: `iwdd-${athleteData.id}`
      };
    });
    setOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrants]);

  useEffect(() => {
    // initiate module with athlete id
    if (config.hasOwnProperty('initiateWithAthleteId') && entrants) {
      const athleteId = getAthleteIdById(entrants, config.initiateWithAthleteId);
      if (athleteId) {
        setAutoChange(false);
        setAthleteId(athleteId);
        setShowDive(false);
      } else {
        console.warn(`athlete id (${config.initiateWithAthleteId}) not found, set first athlete in list`);
        setAthleteId(entrants[0].athlete.id);
      }
    }
  }, [config, entrants]);

  const MotionCard = motion.custom(GuideAnimation);

  const athleteImageSrc = athlete && athlete.athlete && athlete.athlete.image && athlete.athlete.image.profile
    ? athlete.athlete.image.profile
    : './athlete-no-image.png';

  return (
    <>
      {athlete && athlete.athlete && (
        <div
          className={style.athleteProfile}
          key={`${athlete.athlete.first_name}-${athlete.athlete.last_name}`}>
          <div className={`appRow full-width ${style.athleteBackground}`}>
            <motion.div
              variants={athleteAnimation}
              initial="initial"
              animate="enter"
              style={{ backgroundImage: `url(${athleteImageSrc})`}}
              className={`appRow ${style.athlete}`}>
              <div className={style.profile}>
                <div className={style.country}>
                  <img
                    src={CountryFlag(athlete.athlete.nationality.iso3)}
                    alt={athlete.athlete.nationality.iso3}
                  />{' '}
                  {athlete.athlete.nationality.iso3}
                </div>
                <div className={style.firstName}>
                  {athlete.athlete.first_name.toLowerCase()}
                </div>
                <div className={style.lastName}>
                  {athlete.athlete.last_name.toLowerCase()}
                </div>
              </div>
            </motion.div>
          </div>
          {mode !== 'archive' && (
            <div
              className={style.autoChange}
              onClick={() => {
                const dataLayerArgs = {
                  dataLayer: {
                    event: 'rbVideoTracking',
                    eventCategory: 'video',
                    eventAction: 'sidebar',
                    eventLabel: `Auto Update - ${!autoChange ? 'ON' : 'OFF'}`,
                    widgetType: `Athlete Profile - ${
                      config.trackingTitle || config.navigationTitle
                    }`,
                    athleteProfile: `${athlete.athlete.first_name} ${athlete.athlete.last_name}`
                  }
                };
                TagManager.dataLayer(dataLayerArgs);
                setAutoChange(!autoChange);
              }}>
              {!autoChange && <>{getTrans(lang.autoUpdate)}</>}{' '}
              <OnOffSwitch on={autoChange} />
            </div>
          )}
          <ModalSelect
            headerText={getTrans(lang.changeAthlete).toUpperCase()}
            className={`mini ${style.select}`}
            value={{ label: getTrans(lang.changeAthlete), value: `iwdd-${athleteId}` }}
            showSelectAthleteImage={config.showSelectAthleteImage}
            options={options}
            buttonShape="pill"
            onChange={(value) => {
              const newAthlete = entrants.find(
                ({ athlete }) => athlete.id === value.athlete_id
              ).athlete;
              const dataLayerArgs = {
                dataLayer: {
                  event: 'rbVideoTracking',
                  eventCategory: 'video',
                  eventAction: 'sidebar',
                  eventLabel: 'Dropdown - Athlete',
                  widgetType: `Athlete Profile - ${
                    config.trackingTitle || config.navigationTitle
                  }`,
                  athleteProfile: `${newAthlete.first_name} ${newAthlete.last_name}`
                }
              };
              TagManager.dataLayer(dataLayerArgs);

              setAutoChange(false);
              setAthleteId(value.athlete_id);
              setShowDive(false);
            }}
          />
          <div className={`appRow modest-width`}>
            <Cols
              className="mb-3"
              cols={[
                {
                  title: getTrans(lang.age),
                  value: athlete.athlete.age || ''
                },
                {
                  title: getTrans(lang.seriesStanding),
                  value:
                    (typeof config.showStanding === 'undefined' || config.showStanding) &&
                    config.eventType === 'rampage'
                      ? getStartsWins(athlete.career_stats, 'Start')
                      : athlete.hasOwnProperty('series_rankings') &&
                        Array.isArray(athlete.series_rankings) &&
                        athlete.series_rankings.length > 0
                      ? getStanding(athlete.series_rankings[0])
                      : '',
                  className: config.eventType !== 'rampage' ? `text-red` : ''
                },
                {
                  title: athlete.active_dive
                    ? getTrans(lang.divesSince)
                    : getTrans(lang.points),
                  value:
                    config.eventType === 'rampage'
                      ? getStartsWins(athlete.career_stats, 'Win')
                      : athlete.hasOwnProperty('series_rankings') &&
                        !athlete.active_dive &&
                        athlete.series_rankings.length > 0
                      ? athlete.series_rankings[0].points || ''
                      : Array.isArray(athlete.career_stats) && athlete.active_dive
                      ? getDivesSince(athlete.career_stats)
                      : ''
                }
              ]}
            />
            {athlete.active_dive && showDive ? (
              <AnimatePresence>
                <MotionCard
                  exit="exit"
                  initial="enter"
                  animate="center"
                  transition={{
                    x: { type: 'spring', stiffness: 300, damping: 30 },
                    opacity: { duration: 1.2 }
                  }}
                  suffix={athlete.athlete.gender}
                  animationPath={config.animationPath}
                  config={athlete.active_dive}
                  animationDuration={config.animationDuration || 5000}
                  diveDifficulty={getTrans(lang.diveDifficulty)}
                  eventType={config.eventType}
                />
              </AnimatePresence>
            ) : athlete.active_dive && !showDive ? (
              <DiveTeaserCard
                launcher={getTrans(lang.launcher)}
                expand={getTrans(lang.expand)}
                setShowDive={() => {
                  setShowDive(true);
                }}
              />
            ) : (
              ''
            )}
            {!athlete.active_dive && (
              <ResultChart
                data={chartData}
                range={config.resultChartRange}
                title={getTrans(lang.last5Results)}
                noData={getTrans(lang.noData)}
              />
            )}

            {config.showRadar && (
              <RadarChart
                data={radarData}
                title={getTrans(lang.radarAttributes)}
                noData={getTrans(lang.noData)}
                lang={lang}
              />
            )}

            {(!config.hasOwnProperty('showCompareButton') || config.showCompareButton) &&
              <div className="appRow my-10">
                <ModalComparison
                  athleteLeftId={athlete.athlete_id}
                  list={options}
                  config={config}
                  onClick={() => {
                    const dataLayerArgs = {
                      dataLayer: {
                        event: 'rbVideoTracking',
                        eventCategory: 'video',
                        eventAction: 'sidebar',
                        eventLabel: `Compare Athlete`,
                        widgetType: `${config.type} - ${
                          config.trackingTitle || config.navigationTitle
                        }`,
                        athleteProfile: `${athlete.label}`
                      }
                    };
                    TagManager.dataLayer(dataLayerArgs);
                  }}
                />
              </div>
            }

            <div>
              {athlete.active_dive && (
                <ResultChart
                  data={chartData}
                  range={config.resultChartRange}
                  title={getTrans(lang.last5Results)}
                  noData={getTrans(lang.noData)}
                />
              )}

              <Cols
                className={`appRow full-width inner-padding firstRowExtend${
                  !config.showRadar ? ' noBorderTop' : ''
                }`}
                cols={[
                  {
                    title: getTrans(lang.starts),

                    value:
                      config.eventType === 'rampage'
                        ? getStartsWins(athlete.career_stats, 'FirstRampage')
                        : wcStats
                        ? wcStats.starts
                        : ''
                  },
                  {
                    title: getTrans(lang.wins),
                    value:
                      config.eventType === 'rampage'
                        ? getStartsWins(athlete.career_stats, 'Top5')
                        : wcStats
                        ? wcStats.wins
                        : ''
                  },
                  {
                    title: getTrans(lang.podiums),
                    value:
                      config.eventType === 'rampage'
                        ? getStartsWins(athlete.career_stats, 'HighestRampageScore')
                        : wcStats
                        ? wcStats.top5
                        : ''
                  }
                ]}
              />
               <Cols
                className="appRow"
                cols={[
                  {
                    title: getTrans(lang.furtherFacts),
                    value: facts && facts.length ? facts || '' : '',
                    className: `${style.infoText} ${colStyle.infoText}`
                  }
                ]} />
              {!athlete.active_dive && (
                <Cols
                  className="appRow"
                  cols={[
                    {
                      title: getTrans(lang.team),
                      value: athlete.team ? athlete.team.name : '',
                      className: `${style.infoText} ${colStyle.infoText} ${colStyle.teamName}`
                    }
                  ]} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { AthleteProfile };
